/**
 * Which file column fills which field (docs/import/PLAN.md §4.2). A header is compared, without case, accents, spaces
 * or punctuation, with the field's own title (`importColumns` `text`, already in the user's language), its name
 * (`value`) and the field's usual names in the app's 24 languages below; then with the choices this person made for
 * the same header last time, which win. A field fills one column at most: the first header that names it.
 */
import type { ImportColumn } from '../list/transfer-api'

/** `"E-mail adresa"` → `emailadresa`: lower case, no accents, letters and digits only (any script). */
export function normalizeHeader(text: string): string {
  return text
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]/gu, '')
}

/**
 * Usual column names per API field, all languages together (normalised when compared). The fields of every import
 * (candidates, employees, contacts) share one table; a word listed under two fields serves the one the import has.
 */
export const FIELD_SYNONYMS: Record<string, string[]> = {
  Gender: [
    'gender',
    'sex',
    'genre',
    'sexe',
    'geschlecht',
    'género',
    'genero',
    'sexo',
    'genere',
    'sesso',
    'geslacht',
    'płeć',
    'pohlaví',
    'pohlavie',
    'gen',
    'nem',
    'kön',
    'køn',
    'sukupuoli',
    'sugu',
    'dzimums',
    'lytis',
    'spol',
    'пол',
    'φύλο',
    'cinsiyet',
  ],
  FirstName: [
    'first name',
    'firstname',
    'given name',
    'forename',
    'prénom',
    'prenom',
    'vorname',
    'nombre',
    'nome',
    'voornaam',
    'imię',
    'imie',
    'jméno',
    'meno',
    'prenume',
    'keresztnév',
    'utónév',
    'förnamn',
    'fornavn',
    'etunimi',
    'eesnimi',
    'vārds',
    'vardas',
    'ime',
    'име',
    'όνομα',
    'ad',
  ],
  LastName: [
    'last name',
    'lastname',
    'surname',
    'family name',
    'nom',
    'nom de famille',
    'nachname',
    'familienname',
    'apellido',
    'apellidos',
    'cognome',
    'sobrenome',
    'apelido',
    'achternaam',
    'nazwisko',
    'příjmení',
    'priezvisko',
    'nume',
    'vezetéknév',
    'efternamn',
    'efternavn',
    'sukunimi',
    'perekonnanimi',
    'uzvārds',
    'pavardė',
    'prezime',
    'priimek',
    'презиме',
    'фамилия',
    'επώνυμο',
    'soyad',
    'soyadı',
  ],
  Email: [
    'email',
    'e-mail',
    'mail',
    'email address',
    'e-mail address',
    'courriel',
    'adresse e-mail',
    'adresse email',
    'correo',
    'correo electrónico',
    'posta elettronica',
    'e-mailadres',
    'adres e-mail',
    'e-mailová adresa',
    'e-posta',
    'e-post',
    'e-postadress',
    'sähköposti',
    'e-pasts',
    'el. paštas',
    'el paštas',
    'е-поща',
    'имейл',
    'е-пошта',
    'ηλεκτρονικό ταχυδρομείο',
  ],
  Phone: [
    'phone',
    'phone number',
    'telephone',
    'mobile',
    'cell',
    'téléphone',
    'portable',
    'telefon',
    'telefonnummer',
    'handy',
    'teléfono',
    'telefono',
    'móvil',
    'cellulare',
    'telefone',
    'telemóvel',
    'telefoon',
    'mobiel',
    'telefon komórkowy',
    'komórka',
    'mobil',
    'telefonszám',
    'puhelin',
    'puhelinnumero',
    'tālrunis',
    'telefonas',
    'телефон',
    'τηλέφωνο',
    'gsm',
  ],
  BirthDate: [
    'birth date',
    'birthdate',
    'date of birth',
    'dob',
    'birthday',
    'date de naissance',
    'geburtsdatum',
    'fecha de nacimiento',
    'data di nascita',
    'data de nascimento',
    'geboortedatum',
    'data urodzenia',
    'datum narození',
    'dátum narodenia',
    'data nașterii',
    'data nasterii',
    'születési dátum',
    'születési idő',
    'födelsedatum',
    'fødselsdato',
    'syntymäaika',
    'sünniaeg',
    'dzimšanas datums',
    'gimimo data',
    'datum rođenja',
    'datum rojstva',
    'дата на раждане',
    'датум рођења',
    'ημερομηνία γέννησης',
    'doğum tarihi',
  ],
  Address: [
    'address',
    'street',
    'adresse',
    'rue',
    'anschrift',
    'straße',
    'strasse',
    'dirección',
    'direccion',
    'indirizzo',
    'endereço',
    'morada',
    'adres',
    'straat',
    'ulica',
    'adresa',
    'ulice',
    'cím',
    'osoite',
    'aadress',
    'adrese',
    'адрес',
    'адреса',
    'διεύθυνση',
    'naslov',
  ],
  City: [
    'city',
    'town',
    'ville',
    'stadt',
    'ort',
    'ciudad',
    'città',
    'citta',
    'cidade',
    'plaats',
    'stad',
    'miasto',
    'miejscowość',
    'město',
    'mesto',
    'oraș',
    'oras',
    'város',
    'by',
    'kaupunki',
    'linn',
    'pilsēta',
    'miestas',
    'grad',
    'град',
    'πόλη',
    'şehir',
    'sehir',
  ],
  Zip: [
    'zip',
    'zip code',
    'postcode',
    'postal code',
    'code postal',
    'cp',
    'plz',
    'postleitzahl',
    'código postal',
    'codigo postal',
    'cap',
    'kod pocztowy',
    'psč',
    'psc',
    'cod poștal',
    'irányítószám',
    'postnummer',
    'postinumero',
    'postiindeks',
    'pasta indekss',
    'pašto kodas',
    'poštanski broj',
    'poštna številka',
    'пощенски код',
    'поштански број',
    'ταχυδρομικός κώδικας',
    'posta kodu',
  ],
  Country: [
    'country',
    'pays',
    'land',
    'staat',
    'país',
    'pais',
    'paese',
    'nazione',
    'kraj',
    'země',
    'krajina',
    'țară',
    'tara',
    'ország',
    'maa',
    'riik',
    'valsts',
    'šalis',
    'država',
    'държава',
    'држава',
    'χώρα',
    'ülke',
  ],
  Position: [
    'position',
    'job title',
    'job',
    'role',
    'poste',
    'fonction',
    'métier',
    'intitulé du poste',
    'stelle',
    'berufsbezeichnung',
    'jobtitel',
    'puesto',
    'cargo',
    'posizione',
    'ruolo',
    'mansione',
    'função',
    'functie',
    'stanowisko',
    'pozice',
    'pracovní pozice',
    'pozícia',
    'funcție',
    'post',
    'pozíció',
    'munkakör',
    'befattning',
    'tjänst',
    'stilling',
    'tehtävä',
    'ametikoht',
    'amats',
    'pareigos',
    'radno mjesto',
    'delovno mesto',
    'длъжност',
    'позиција',
    'θέση',
    'pozisyon',
    'unvan',
  ],
  Skills: [
    'skills',
    'skill',
    'competences',
    'compétences',
    'kenntnisse',
    'fähigkeiten',
    'kompetenzen',
    'habilidades',
    'competencias',
    'competenze',
    'abilità',
    'competências',
    'vaardigheden',
    'umiejętności',
    'dovednosti',
    'zručnosti',
    'competențe',
    'készségek',
    'färdigheter',
    'kompetencer',
    'taidot',
    'oskused',
    'prasmes',
    'įgūdžiai',
    'vještine',
    'veščine',
    'умения',
    'вештине',
    'δεξιότητες',
    'beceriler',
    'yetenekler',
  ],
  StartDate: [
    'start date',
    'hire date',
    'date of hire',
    'start',
    'date d’entrée',
    "date d'entrée",
    "date d'embauche",
    'date de début',
    'eintrittsdatum',
    'startdatum',
    'fecha de alta',
    'fecha de inicio',
    'data di assunzione',
    'data di inizio',
    'data de admissão',
    'data de início',
    'datum in dienst',
    'begindatum',
    'data zatrudnienia',
    'data rozpoczęcia',
    'datum nástupu',
    'dátum nástupu',
    'data angajării',
    'belépés dátuma',
    'kezdő dátum',
    'anställningsdatum',
    'ansættelsesdato',
    'aloituspäivä',
    'tööle asumise kuupäev',
    'darba sākuma datums',
    'įdarbinimo data',
    'datum zaposlenja',
    'datum zaposlitve',
    'дата на постъпване',
    'датум запослења',
    'ημερομηνία πρόσληψης',
    'işe başlama tarihi',
  ],
  EndDate: [
    'end date',
    'leaving date',
    'date de fin',
    'date de sortie',
    'austrittsdatum',
    'enddatum',
    'fecha de baja',
    'fecha de fin',
    'data di cessazione',
    'data de saída',
    'einddatum',
    'datum uit dienst',
    'data zakończenia',
    'datum ukončení',
    'dátum ukončenia',
    'data încetării',
    'kilépés dátuma',
    'slutdatum',
    'fratrædelsesdato',
    'päättymispäivä',
    'lõppkuupäev',
    'beigu datums',
    'pabaigos data',
    'datum prestanka',
    'datum prenehanja',
    'дата на напускане',
    'датум престанка',
    'ημερομηνία λήξης',
    'işten çıkış tarihi',
    'bitiş tarihi',
  ],
  EvaluationPeriodEnd: [
    'evaluation period end',
    'end of probation',
    'probation end',
    'fin de période d’essai',
    "fin de période d'essai",
    "fin de la période d'essai",
    'ende der probezeit',
    'probezeitende',
    'fin del período de prueba',
    'fine periodo di prova',
    'fim do período experimental',
    'einde proeftijd',
    'koniec okresu próbnego',
    'konec zkušební doby',
    'koniec skúšobnej doby',
    'sfârșitul perioadei de probă',
    'próbaidő vége',
    'prövotidens slut',
    'prøvetidens udløb',
    'koeajan päättyminen',
    'katseaja lõpp',
    'pārbaudes laika beigas',
    'bandomojo laikotarpio pabaiga',
    'kraj probnog rada',
    'konec poskusne dobe',
    'край на изпитателния срок',
    'крај пробног рада',
    'λήξη δοκιμαστικής περιόδου',
    'deneme süresi sonu',
  ],
  NetSalary: [
    'net salary',
    'net pay',
    'salaire net',
    'nettogehalt',
    'salario neto',
    'stipendio netto',
    'salário líquido',
    'nettosalaris',
    'wynagrodzenie netto',
    'čistá mzda',
    'salariu net',
    'nettó fizetés',
    'nettolön',
    'nettoløn',
    'nettopalkka',
    'netopalk',
    'neto alga',
    'grynasis atlyginimas',
    'neto plaća',
    'neto plača',
    'нетна заплата',
    'нето плата',
    'καθαρός μισθός',
    'net maaş',
  ],
  GrossSalary: [
    'gross salary',
    'gross pay',
    'salary',
    'salaire brut',
    'salaire',
    'bruttogehalt',
    'gehalt',
    'salario bruto',
    'salario',
    'stipendio lordo',
    'retribuzione lorda',
    'salário bruto',
    'brutosalaris',
    'salaris',
    'wynagrodzenie brutto',
    'wynagrodzenie',
    'hrubá mzda',
    'mzda',
    'salariu brut',
    'bruttó fizetés',
    'bruttolön',
    'lön',
    'bruttoløn',
    'løn',
    'bruttopalkka',
    'palkka',
    'brutopalk',
    'palk',
    'bruto alga',
    'alga',
    'bruto atlyginimas',
    'atlyginimas',
    'bruto plaća',
    'plaća',
    'bruto plača',
    'plača',
    'брутна заплата',
    'заплата',
    'бруто плата',
    'плата',
    'μικτός μισθός',
    'μισθός',
    'brüt maaş',
    'maaş',
  ],
  Teams: [
    'team',
    'teams',
    'team(s)',
    'équipe',
    'équipes',
    'abteilung',
    'equipo',
    'equipos',
    'squadra',
    'squadre',
    'equipa',
    'equipas',
    'zespół',
    'zespoły',
    'dział',
    'tým',
    'týmy',
    'tím',
    'tímy',
    'echipă',
    'echipe',
    'csapat',
    'csapatok',
    'avdelning',
    'hold',
    'tiimi',
    'tiimit',
    'meeskond',
    'komanda',
    'komandos',
    'tim',
    'timovi',
    'ekipa',
    'ekipe',
    'екип',
    'екипи',
    'тим',
    'тимови',
    'ομάδα',
    'ομάδες',
    'takım',
    'takımlar',
    'ekip',
  ],
  Name: [
    'name',
    'company',
    'company name',
    'organisation',
    'organization',
    'raison sociale',
    'société',
    'entreprise',
    'nom',
    'nom de l’entreprise',
    "nom de l'entreprise",
    'firma',
    'firmenname',
    'unternehmen',
    'empresa',
    'razón social',
    'nombre',
    'azienda',
    'ragione sociale',
    'nome',
    'bedrijf',
    'bedrijfsnaam',
    'naam',
    'nazwa',
    'nazwa firmy',
    'název',
    'název firmy',
    'názov',
    'názov firmy',
    'companie',
    'denumire',
    'cég',
    'cégnév',
    'név',
    'företag',
    'företagsnamn',
    'namn',
    'virksomhed',
    'navn',
    'yritys',
    'yrityksen nimi',
    'nimi',
    'ettevõte',
    'uzņēmums',
    'nosaukums',
    'įmonė',
    'pavadinimas',
    'tvrtka',
    'naziv',
    'podjetje',
    'ime',
    'фирма',
    'име',
    'компания',
    'назив',
    'εταιρεία',
    'επωνυμία',
    'şirket',
    'şirket adı',
  ],
  CompanyNumber: [
    'company number',
    'registration number',
    'company registration number',
    'company id',
    'siren',
    'siret',
    'numéro siren',
    'numéro siret',
    'handelsregisternummer',
    'registernummer',
    'cif',
    'nif',
    'número de registro',
    'numero rea',
    'kvk',
    'kvk-nummer',
    'regon',
    'krs',
    'ičo',
    'ico',
    'ičo firmy',
    'cui',
    'nr. reg. com.',
    'cégjegyzékszám',
    'organisationsnummer',
    'orgnr',
    'cvr',
    'cvr-nummer',
    'y-tunnus',
    'registrikood',
    'reģistrācijas numurs',
    'įmonės kodas',
    'oib',
    'matični broj',
    'matična številka',
    'еик',
    'булстат',
    'матични број',
    'γεμη',
    'αριθμός γεμη',
    'mersis no',
    'ticaret sicil no',
  ],
  VatNumber: [
    'vat',
    'vat number',
    'vat no',
    'vat id',
    'tva',
    'numéro de tva',
    'n° tva',
    'tva intracommunautaire',
    'ust-idnr',
    'ust-id',
    'umsatzsteuer-id',
    'iva',
    'nif-iva',
    'partita iva',
    'p.iva',
    'btw',
    'btw-nummer',
    'nip',
    'dič',
    'dic',
    'ič dph',
    'cod tva',
    'közösségi adószám',
    'momsnummer',
    'momsregistreringsnummer',
    'alv-numero',
    'alv-tunniste',
    'kmkr',
    'pvn numurs',
    'pvm kodas',
    'pdv broj',
    'id za ddv',
    'ддс номер',
    'ид номер по ддс',
    'пиб',
    'αφμ',
    'vergi no',
    'vergi numarası',
  ],
  LegalRepresentative: [
    'legal representative',
    'representative',
    'contact person',
    'contact',
    'représentant légal',
    'représentant',
    'gesetzlicher vertreter',
    'vertreter',
    'ansprechpartner',
    'representante legal',
    'representante',
    'persona de contacto',
    'rappresentante legale',
    'referente',
    'wettelijk vertegenwoordiger',
    'contactpersoon',
    'przedstawiciel',
    'osoba kontaktowa',
    'zástupce',
    'kontaktní osoba',
    'zástupca',
    'reprezentant legal',
    'persoană de contact',
    'törvényes képviselő',
    'kapcsolattartó',
    'firmatecknare',
    'kontaktperson',
    'tegningsberettiget',
    'edustaja',
    'yhteyshenkilö',
    'esindaja',
    'kontaktisik',
    'pārstāvis',
    'kontaktpersona',
    'atstovas',
    'kontaktinis asmuo',
    'zakonski zastupnik',
    'kontakt osoba',
    'zakoniti zastopnik',
    'kontaktna oseba',
    'представител',
    'лице за контакт',
    'законски заступник',
    'νόμιμος εκπρόσωπος',
    'υπεύθυνος επικοινωνίας',
    'yasal temsilci',
    'yetkili kişi',
  ],
  IsClient: [
    'client',
    'customer',
    'is client',
    'est client',
    'kunde',
    'cliente',
    'klant',
    'klient',
    'zákazník',
    'ügyfél',
    'vevő',
    'asiakas',
    'klientas',
    'klijent',
    'stranka',
    'клиент',
    'клијент',
    'πελάτης',
    'müşteri',
  ],
  IsSupplier: [
    'supplier',
    'vendor',
    'is supplier',
    'fournisseur',
    'lieferant',
    'proveedor',
    'fornitore',
    'fornecedor',
    'leverancier',
    'dostawca',
    'dodavatel',
    'furnizor',
    'szállító',
    'beszállító',
    'leverantör',
    'leverandør',
    'toimittaja',
    'tarnija',
    'piegādātājs',
    'tiekėjas',
    'dobavljač',
    'dobavitelj',
    'доставчик',
    'добављач',
    'προμηθευτής',
    'tedarikçi',
  ],
}

/**
 * Synonym (normalised) → its fields, built once. A word can name different fields in different imports ("Nom" is a
 * person's last name, a company's name): the first one this import has wins.
 */
const BY_SYNONYM = new Map<string, string[]>()
for (const [field, words] of Object.entries(FIELD_SYNONYMS)) {
  for (const word of words) {
    const key = normalizeHeader(word)
    if (!key) continue
    const fields = BY_SYNONYM.get(key) ?? []
    if (!fields.includes(field)) BY_SYNONYM.set(key, [...fields, field])
  }
}

/** Remembered choices: normalised header → field, or `''` for "not imported". */
export type RememberedMapping = Record<string, string>

/**
 * The field of each header (`null` = not imported): the remembered choice, else the field whose title, name or usual
 * name the header is. Only the fields of this import are used, each once.
 */
export function autoMatch(
  headers: readonly string[],
  columns: readonly ImportColumn[],
  remembered: RememberedMapping = {},
): Array<string | null> {
  const known = new Set(columns.map((column) => column.value))
  const byTitle = new Map<string, string>()
  for (const column of columns) {
    byTitle.set(normalizeHeader(column.text), column.value)
    byTitle.set(normalizeHeader(column.value), column.value)
  }
  const used = new Set<string>()
  const pick = (field: string | undefined): string | null => {
    if (!field || !known.has(field) || used.has(field)) return null
    used.add(field)
    return field
  }
  const keys = headers.map(normalizeHeader)
  // Remembered choices first, so a guess cannot take a field this person gave to another column.
  const result: Array<string | null | undefined> = keys.map((key) =>
    key in remembered ? (remembered[key] ? pick(remembered[key]) : null) : undefined,
  )
  return result.map((value, index) => {
    if (value !== undefined) return value
    const key = keys[index] ?? ''
    if (!key) return null
    const title = pick(byTitle.get(key))
    if (title) return title
    for (const field of BY_SYNONYM.get(key) ?? []) {
      const synonym = pick(field)
      if (synonym) return synonym
    }
    return null
  })
}

/** What to remember of a mapping: each named header's choice. */
export function rememberMapping(headers: readonly string[], targets: ReadonlyArray<string | null>): RememberedMapping {
  const memory: RememberedMapping = {}
  headers.forEach((header, index) => {
    const key = normalizeHeader(header)
    if (key) memory[key] = targets[index] ?? ''
  })
  return memory
}
